import React from 'react'
import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { Box, Typography } from '@mui/material'
import { UseFetchProps } from '@e3dc-react/shell/Hooks/useFetch'
import { makeStyles } from 'tss-react/mui'
import { useParams } from 'react-router-dom'
import { DEFAULT_HUMAN_TIME, format } from '@e3dc-react/shell/Libs/date'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { EmployeeHistory, ActionKey } from '../types/employee-history'

const actionKeyToTranslationKey: Record<ActionKey, string> = {
  INVITED: 'invited',
  REMOVED: 'removed',
  MADE_TO_MANAGER: 'made-to-manager',
  MADE_TO_INSTALLER: 'made-to-installer',
  MADE_TO_PARTNER: 'made-to-partner',
}

const useStyles = makeStyles()(theme => ({
  date: {
    minWidth: '125px',
  },
  title: {
    fontWeight: theme.typography.subtitle2.fontWeight,
  },
}))

/**
 * This hook holds all data for the employee history api view
 */
export const useEmployeeHistoryTableData = (): {
  fetchProps: UseFetchProps
  columns: ApiTableColumn<EmployeeHistory>[]
} => {
  const { t } = useCustomTranslation()
  const { id } = useParams<{ id: string }>()
  const companyId = Number(id)
  const { classes } = useStyles()

  const fetchProps: FetchProps = {
    url: `users/companies/${companyId}/employeeHistory`,
    orderBy: '-date',
  }

  const columns: ApiTableColumn<EmployeeHistory>[] = [
    {
      name: 'date',
      label: t('administration.companies.empl-history.date'),
      value: item => <Box className={classes.date}>{format(item['date'], `${t('common.format.date-fns-date')} - ${DEFAULT_HUMAN_TIME}`)}</Box>,
    },
    {
      name: 'employeeName',
      label: t('administration.companies.empl-history.employee'),
      value: item => (
        <Box>
          <Typography variant="subtitle1" className={classes.title}>
            {item.employeeName}
          </Typography>
          <Typography variant="subtitle2">{item.employeeEmail}</Typography>
        </Box>
      ),
    },
    {
      name: 'action',
      label: t('administration.companies.empl-history.action'),
      value: item => {
        const actionKey = item.action

        return t(`administration.companies.empl-history.actions.${actionKeyToTranslationKey[actionKey]}`)
      },
    },
    {
      name: 'authorizedName',
      label: t('administration.companies.empl-history.authorized'),
      value: item => (
        <Box>
          <Typography variant="subtitle1" className={classes.title}>
            {item.authorizedName}
          </Typography>
          <Typography variant="subtitle2">{item.authorizedEmail}</Typography>
        </Box>
      ),
    },
    {
      name: 'reason',
      label: t('administration.companies.empl-history.reason'),
      value: item => item.reason,
    },
  ]

  return { columns, fetchProps }
}
